import React, { useState, useCallback, useEffect } from "react";
import { Button, Box, CircularProgress, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { studentAddedNotification, trainingCompletionNotification } from "helpers/batch_helper";
import { toast } from "react-toastify";
import { useParams } from 'react-router-dom'; // Import useParams

const NotificationButtons = () => { // Removed batchId prop

    const params = useParams(); // Get batchId from URL parameters

    useEffect(() => {
        console.log("batchId: " + params?.id)
    }, [])

    // State for loading indicators
    const [loadingStudentNotification, setLoadingStudentNotification] = useState(false);
    const [loadingCompletionNotification, setLoadingCompletionNotification] = useState(false);

    // --- API Call Functions ---

    const handleStudentDetailsNotification = useCallback(async () => {
        // Check if batchId is available from URL parameters
        if (!params.id) {
            toast.error('Batch ID is missing in URL parameters. Please navigate to a valid batch page.');
            return;
        }
        setLoadingStudentNotification(true);

        try {
            // Assuming studentAddedNotification handles the API call and returns { success: boolean, message: string }
            const response = await studentAddedNotification(params.id);
            if (response?.success) {
                // Use the message from the API response for success toast
                toast.success(response?.message || "Student details notification sent successfully!");
            } else {
                // Handle cases where success is false but no exception occurred
                toast.error(response?.message || "Failed to send student details notification.");
            }
        } catch (error) {
            console.error("Error in handleStudentDetailsNotification:", error);
            // Extract a more user-friendly error message
            const errorMessage = error?.response?.data?.message || error?.message || "An unexpected error occurred.";
            toast.error(`Failed to send student details notification: ${errorMessage}`);
        } finally {
            setLoadingStudentNotification(false);
        }
    }, [params?.id]); // Dependency: batchId (from useParams)

    const handleTrainingCompletionNotification = useCallback(async () => {
        // Check if batchId is available from URL parameters
        if (!params.id) {
            toast.error('Batch ID is missing in URL parameters for training completion notification.');
            return;
        }
        setLoadingCompletionNotification(true);

        try {
            // Assuming trainingCompletionNotification handles the API call and returns { success: boolean, message: string }
            const response = await trainingCompletionNotification(params.id);
            if (response?.success) {
                // Use the message from the API response for success toast
                toast.success(response?.message || "Training completion notification sent successfully!");
            } else {
                // Handle cases where success is false but no exception occurred
                toast.error(response?.message || "Failed to send training completion notification.");
            }
        } catch (error) {
            console.error("Error in handleTrainingCompletionNotification:", error);
            // Extract a more user-friendly error message
            const errorMessage = error?.response?.data?.message || error?.message || "An unexpected error occurred.";
            toast.error(`Failed to send training completion notification: ${errorMessage}`);
        } finally {
            setLoadingCompletionNotification(false);
        }
    }, [params?.id]); // Dependency: batchId (from useParams)

    // Effect to potentially clear toast messages if batchId changes (optional)
    // Note: Clearing toasts automatically based on batchId changes can be complex
    // without managing toast IDs. For now, new toasts will appear.
    // useEffect(() => {
    //     // Logic to potentially clear old toasts if desired and feasible
    // }, [batchId]);


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            <Button
                variant="contained"
                startIcon={loadingStudentNotification ? <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} /> : <SendIcon />}
                onClick={handleStudentDetailsNotification}
                // Disable button if batchId is not loaded from URL params or if another button is loading
                disabled={!params?.id || loadingStudentNotification || loadingCompletionNotification}
                color="primary"
                sx={{ textTransform: 'none' }}
            >
                Notify PM of Student Updates
            </Button>

            <Button
                variant="outlined"
                startIcon={loadingCompletionNotification ? <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} /> : <AssignmentTurnedInIcon />}
                onClick={handleTrainingCompletionNotification}
                // Disable button if batchId is not loaded from URL params or if another button is loading
                disabled={!params?.id || loadingStudentNotification || loadingCompletionNotification}
                color="secondary"
                sx={{ textTransform: 'none' }}
            >
                Notify PM of Training Completion
            </Button>
        </Box>
    );
};

export default NotificationButtons;